import { FC } from 'react';
import styled from '@emotion/styled';
import TestSeriesCard from '@comp/TestSeriesCard/TestSeriesCard';
import { Spacings } from '@styles/index';
import { isCompleted } from '@models/Course';
import { segment } from '@actions/analytics';
import { GOAL_PAGE, SEE_ALL_CLICKED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import CardCarousel from './CardCarousel';

const SecWrapper = styled.section`
  position: relative;
  margin: auto;
  margin-top: 120px;
  padding-bottom: 32px;
  width: 1376px;
  overflow: hidden;
  @media (max-width: 70.95em) {
    width: 728px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    margin-top: 56px;
    padding: 0;
  }
`;

const linkStyle = `
  flex: 1 0 auto;
  width: 272px;
  height: 188px;
  margin-right: ${Spacings.SPACING_6B};
  @media (max-width: 47.95em) {
    margin-right:16px;
  }
`;

type CardProps = {
  test: {
    [key: string]: any;
  };
  dataAnalytics?: string;
  index: number;
  label: string;
};

const Card: FC<CardProps> = ({ test, dataAnalytics = '', index, label }) => {
  const nextTrack =
    test.nextTrack || test.nextSession || test.nextClass || test.nextItem;
  return (
    <TestSeriesCard
      key={test.uid}
      test={{
        ...test,
        authors: test?.authors?.map((author) => ({
          ...author,
          name: author.name || `${author.firstName} ${author.lastName}`
        })),
        isCompleted: test.isCompleted || isCompleted(test),
        nextTrack: {
          ...nextTrack,
          ...nextTrack?.properties
        }
      }}
      linkStyle={linkStyle}
      dataAnalytics={dataAnalytics}
      index={index}
    />
  );
};

type TestSeriesSectionProps = {
  block: {
    data: any[];
  };
  href?: string;
  as?: string;
  goalDetails?: {
    name?: string;
    uid?: string;
  };
};

const TestSeriesSection: FC<TestSeriesSectionProps> = ({
  block,
  href = null,
  as = null,
  goalDetails = {}
}) => {
  const { name, uid } = goalDetails;
  const { data: testSeries } = block;
  const label = 'Track your preparation';
  const goalEvent = useGoalEventProps({ goalUID: uid });

  const seeAllClickEvent = () => {
    segment.track(SEE_ALL_CLICKED, {
      ...goalEvent,
      last_primary_source: GOAL_PAGE,
      last_primary_source_section: label
    });
  };
  return (
    <SecWrapper>
      <CardCarousel
        label={label}
        subLabel={`Evaluate your progress with our ${name} Mock Tests and detailed results analysis`}
        step={4}
        href={href}
        as={as}
        rightShadowDiv
        dataAnalytics="testSeries"
        type="test-series"
        handleSeeAll={seeAllClickEvent}
      >
        {testSeries.map((testSerie, index) => (
          <Card
            index={index}
            key={testSerie.uid}
            test={testSerie}
            label={label}
            dataAnalytics="subscription-testSeries"
          />
        ))}
      </CardCarousel>
    </SecWrapper>
  );
};

export default TestSeriesSection;
